/**
 * Resolves the customer-facing term for a relay load ("Panel"/"Face"/"Zone")
 * for the customer currently in scope.
 *
 * Three-layer fallback: the scope customer's CustomerType override, then the
 * tenant-wide TenantConfig term, then the i18n default. The CustomerType term
 * is BE-authoritative data delivered once per session in the reference
 * catalog; this hook reads the scope customer's customer_type ref, resolves
 * it against that catalog, and hands the row (plus the tenant slice's info
 * object) to the pure resolveRelayLoadTerm ladder, which treats each raw term
 * as an i18n key (literal-as-fallback) rather than raw display text.
 * Surrounding sentence copy stays in i18n; only the noun comes from here.
 *
 * The scope customer comes from the app-layer ScopeContext, which every scope
 * route family provides — the customer/site-group scope Layouts and the
 * site-dashboard layout alike — so this hook reads that one channel. The
 * tenant layer comes from the tenant slice (selectTenantInfo), seeded from
 * TENANT_DEFAULTS so a term always resolves even before the /tenant/ fetch
 * completes.
 *
 * Safe outside customer scope: staff/global grids rendered above any scope
 * Layout (context null) fall back to the tenant term, then the i18n default —
 * the hook never throws. Callers that must keep a non-default fallback (e.g.
 * a surface whose legacy default was "Zones") pass their own fallback key pair.
 *
 * Pairs with features/reference (selectCustomerTypeById), features/tenant
 * (selectTenantInfo), and hooks/relayLoadTerm.
 */
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ScopeContext from '@sophocles/app/ScopeContext';
import { selectCustomerTypeById } from '@sophocles/features/reference';
import { selectTenantInfo } from '@sophocles/features/tenant/slice';
import { resolveRelayLoadTerm, resolveScopeCustomerTypeId } from '@sophocles/hooks/relayLoadTerm';

const DEFAULT_TERM_KEYS = Object.freeze({
  singularKey: 'terms.relayLoad.face',
  pluralKey: 'terms.relayLoad.faces',
});

/**
 * @param {{ singularKey?: string, pluralKey?: string }} [fallbackKeys] - i18n
 *   keys for the no-term default term pair. Defaults to the shared
 *   terms.relayLoad.face* catalog keys ("Face"/"Faces"); override per-call to
 *   preserve a surface-specific default.
 * @param {?object} [customer] - plain-JS customer to resolve against instead of
 *   the ScopeContext read. Flat routes that seed their own customer (the
 *   services tier) pass the customer they fetched; scope-Layout descendants
 *   omit it.
 * @returns {{ singular: string, plural: string }} The relay-load term pair for
 *   the customer (or the tenant/i18n defaults when none applies).
 */
export default function useRelayLoadTerm(fallbackKeys = DEFAULT_TERM_KEYS, customer = null) {
  const { t } = useTranslation();
  // Read ScopeContext directly (not useScope(), which throws off-route) so the
  // hook resolves to null — and thus the tenant/i18n layers — on staff/global
  // surfaces mounted above any scope Layout.
  const scope = useContext(ScopeContext);
  const customerTypeId = resolveScopeCustomerTypeId(customer ? { customer } : scope);
  const catalogRow = useSelector(selectCustomerTypeById(customerTypeId));
  const tenantInfo = useSelector(selectTenantInfo);

  const singularDefault = t(fallbackKeys.singularKey);
  const pluralDefault = t(fallbackKeys.pluralKey);

  return useMemo(
    () => resolveRelayLoadTerm(catalogRow, tenantInfo, { singular: singularDefault, plural: pluralDefault }, t),
    [catalogRow, tenantInfo, singularDefault, pluralDefault, t],
  );
}
